// Detailseite für ein einzelnes Ereignis.
//
// - Stammdaten (Titel, Beschreibung, Autor, Mandant) inline editierbar
//   und per Speichern-Button via PUT zurückschreiben.
// - Status bleibt separat im Header (Inline-PATCH, sofortige Wirkung) –
//   analog zum aktiv/inaktiv-Toggle in MandantDetail.
// - Löschen mit Redirect zurück zur Liste.

import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { api } from '../api.js';
import StatusBadge from './StatusBadge.jsx';

const STATUSES = ['offen', 'in Arbeit', 'erledigt'];

const EMPTY_FORM = {
    title: '',
    description: '',
    author: '',
    mandant_id: '',
};

export default function EventDetail() {
    const { id: idParam } = useParams();
    const navigate = useNavigate();
    const id = Number(idParam);

    const [data, setData] = useState(null);
    const [form, setForm] = useState(EMPTY_FORM);
    const [mandantenList, setMandantenList] = useState([]);
    const [userOptions, setUserOptions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const [saveError, setSaveError] = useState('');
    const [saving, setSaving] = useState(false);
    const [savedNotice, setSavedNotice] = useState(false);

    const load = useCallback(async () => {
        try {
            setLoadError('');
            const d = await api.getEvent(id);
            setData(d);
            setForm({
                title: d.title ?? '',
                description: d.description ?? '',
                author: d.author ?? '',
                mandant_id: d.mandant_id == null ? '' : String(d.mandant_id),
            });
        } catch (err) {
            setLoadError(err.message);
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        load();
        api.listMandanten().then(setMandantenList).catch(() => { /* unkritisch */ });
        api.listUserOptions().then(setUserOptions).catch(() => { /* unkritisch */ });
    }, [load]);

    function updateField(field, value) {
        setForm((f) => ({ ...f, [field]: value }));
        setSavedNotice(false);
    }

    async function handleSave(e) {
        e.preventDefault();
        setSaving(true);
        setSaveError('');
        setSavedNotice(false);
        try {
            const payload = {
                title: form.title,
                description: form.description,
                author: form.author,
                mandant_id: form.mandant_id === '' ? null : Number(form.mandant_id),
            };
            const updated = await api.updateEvent(id, payload);
            setData(updated);
            setSavedNotice(true);
        } catch (err) {
            setSaveError(err.message);
        } finally {
            setSaving(false);
        }
    }

    async function handleStatusChange(status) {
        try {
            const updated = await api.updateEventStatus(id, status);
            setData(updated);
        } catch (err) {
            alert('Status-Update fehlgeschlagen: ' + err.message);
        }
    }

    async function handleDelete() {
        if (!window.confirm('Dieses Ereignis wirklich löschen?')) return;
        try {
            await api.deleteEvent(id);
            navigate('/events');
        } catch (err) {
            alert('Löschen fehlgeschlagen: ' + err.message);
        }
    }

    if (loading) return <div className="empty">Lade…</div>;
    if (loadError) {
        return (
            <div>
                <Link to="/events">← Zurück zur Liste</Link>
                <div className="panel" style={{ color: 'var(--color-danger)', marginTop: 12 }}>
                    Fehler: {loadError}
                </div>
            </div>
        );
    }

    return (
        <div>
            <div style={{ marginBottom: 8 }}>
                <Link to="/events">← Zurück zur Liste</Link>
            </div>
            <div className="toolbar">
                <h1 style={{ margin: 0 }}>{data.title}</h1>
                <StatusBadge status={data.status} />
                <div className="spacer" />
                <select
                    className="status-select"
                    value={data.status}
                    onChange={(e) => handleStatusChange(e.target.value)}
                    title="Status sofort ändern"
                >
                    {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <button className="btn btn--danger" onClick={handleDelete}>
                    Ereignis löschen
                </button>
            </div>

            <form className="panel" onSubmit={handleSave}>
                <h2>Bearbeiten</h2>
                {saveError && <div className="login__error">{saveError}</div>}
                {savedNotice && (
                    <div className="panel" style={{ color: 'var(--color-success)', marginBottom: 12, padding: 8 }}>
                        Gespeichert.
                    </div>
                )}

                <div className="field">
                    <label>Titel *</label>
                    <input
                        type="text"
                        required
                        value={form.title}
                        onChange={(e) => updateField('title', e.target.value)}
                    />
                </div>

                <div className="grid-2">
                    <div className="field">
                        <label>Mandant</label>
                        <select
                            value={form.mandant_id}
                            onChange={(e) => updateField('mandant_id', e.target.value)}
                        >
                            <option value="">– (kein Mandant) –</option>
                            {mandantenList.map((m) => (
                                <option key={m.id} value={m.id}>{m.name}</option>
                            ))}
                        </select>
                    </div>
                    <div className="field">
                        <label>Autor</label>
                        <select
                            value={form.author}
                            onChange={(e) => updateField('author', e.target.value)}
                        >
                            <option value="">– (kein Autor) –</option>
                            {userOptions.map((u) => (
                                <option key={u.id} value={u.username}>{u.username}</option>
                            ))}
                            {/* Falls der gespeicherte Autor nicht (mehr) in der User-Liste
                                steht (gelöscht/umbenannt), zeigen wir ihn trotzdem an, damit
                                er nicht unbemerkt verloren geht. */}
                            {form.author && !userOptions.some((u) => u.username === form.author) && (
                                <option value={form.author}>{form.author}</option>
                            )}
                        </select>
                    </div>
                </div>

                <div className="field">
                    <label>Beschreibung</label>
                    <textarea
                        value={form.description}
                        onChange={(e) => updateField('description', e.target.value)}
                        rows={6}
                    />
                </div>

                <div className="muted" style={{ fontSize: 12, margin: '12px 0' }}>
                    Angelegt am {formatDate(data.created_at)}
                </div>

                <button type="submit" className="btn btn--primary" disabled={saving}>
                    {saving ? 'Speichere…' : 'Speichern'}
                </button>
            </form>
        </div>
    );
}

function formatDate(s) {
    if (!s) return '';
    const d = new Date(s.replace(' ', 'T') + 'Z');
    if (isNaN(d.getTime())) return s;
    return d.toLocaleString('de-DE');
}
